import { Grid } from '@mui/material';
import { type ReactNode, useContext, useState } from 'react';
import { useEffectOnce } from 'react-use';
import type { UpdateUserAvatarInput } from 'src/mutations/avatar/updateUserAvatarMutation';
import { v4 as uniqueKey } from 'uuid';
import * as Yup from 'yup';
import { FormDialogModal, type FormDialogModalProps } from '../FormDialogModal';
import { Cropping } from './Cropping';
import { initialValidationState } from './initialState';
import {
  invalidImageErrorMsg,
  isValidImageExtension,
  UploadFormContent,
} from './UploadFormContent';
import {
  UploadImageModalContext,
  UploadImageModalProvider,
} from './UploadImageModalContext';

// this is needed so we can block the save image button
const EditUploadImageModalSchema = Yup.object({
  imageFile: Yup.mixed()
    .test('is-image', invalidImageErrorMsg, (value) => {
      let result: boolean;
      if (typeof value === 'string') {
        result = isValidImageExtension(value);
      } else if (value === null) {
        result = true;
      } else {
        result = false;
      }
      return result;
    })
    .nullable(),
});

type UploadImageModalSchema = {
  imageUrl?: string | null;
};

type UploadImageModalProps = UploadImageModalSchema & {
  onSubmit?: (data: UpdateUserAvatarInput) => void;
  onClose?: () => void;
  uploading?: boolean;
  open: boolean;
  title: string;
  headline?: string | ReactNode;
  formDialogModalProps?: Partial<
    Omit<
      FormDialogModalProps<typeof EditUploadImageModalSchema>,
      'open' | 'title' | 'onSubmit' | 'onClose' | 'onOpen'
    >
  >;
};

const InnerUploadImageModal = ({
  imageUrl,
  onSubmit,
  onClose,
  open,
  title,
  headline,
  formDialogModalProps,
  uploading,
}: UploadImageModalProps) => {
  const [fileValidation, setFileValidation] = useState<{
    size?: string;
    extension?: string;
    dimension?: string;
  }>(initialValidationState);
  const [modalKey, setModalKey] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [state, dispatch] = useContext(UploadImageModalContext);

  useEffectOnce(() => {
    dispatch({ type: 'IMAGE_URL', payload: imageUrl });
  });

  const resetModal = () => {
    setModalKey(uniqueKey());
    setSelectedFile(null);
    dispatch({ type: 'RESET_MODAL' });
    setFileValidation(initialValidationState);
    if (onClose) {
      onClose();
    }
  };

  const uploadFormContentProps = {
    uploading,
    selectedFile,
    fileValidation,
    setSelectedFile,
    setFileValidation,
  };

  return (
    <FormDialogModal
      key={modalKey}
      formProps={{
        'aria-label': headline ? `${headline}` : 'Upload a new photo',
      }}
      saveButtonProps={{
        label: 'Update',
        ...formDialogModalProps?.saveButtonProps,
      }}
      {...formDialogModalProps}
      title={title}
      headline={headline}
      formSchema={EditUploadImageModalSchema}
      onSubmit={() => {
        if (onSubmit) {
          onSubmit({
            file: state.base64CroppedImage
              ? state.base64CroppedImage
              : state.base64Image,
          });
        }
      }}
      showSecondaryButton={state.cropImage}
      secondaryButtonProps={{
        label: 'Crop Photo',
        disabled: !state.userCropInteraction,
        onClick: () => {
          dispatch({ type: 'TOGGLE_CROP_IMAGE', payload: false });
        },
        ...formDialogModalProps?.secondaryButtonProps,
      }}
      secondaryCancelButtonProps={{
        label: 'Cancel',
        onClick: () => {
          setSelectedFile(null);
          dispatch({ type: 'RESET_MODAL' });
          setFileValidation(initialValidationState);
        },
      }}
      onClose={resetModal}
      open={open}
    >
      <Grid
        size={{ sm: 12 }}
        sx={(theme) => ({
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          flex: '1 1 auto',
          alignItems: 'center',
          paddingY: `${theme.spacing(3)}`,
        })}
      >
        {!state.cropImage ? (
          <UploadFormContent {...uploadFormContentProps} />
        ) : (
          <Cropping />
        )}
      </Grid>
    </FormDialogModal>
  );
};

export const UploadImageModal = (props: UploadImageModalProps) => {
  return (
    <UploadImageModalProvider>
      <InnerUploadImageModal {...props} />
    </UploadImageModalProvider>
  );
};
